import { useEffect } from "react";
import SEOHead from "@/components/SEOHead";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight, MapPin, Video, Target, TrendingUp } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import agenceContenuMontreal from "@/assets/agence-contenu-montreal.jpg";

declare global {
  interface Window {
    Cal?: any;
  }
}

const initCalEmbed = () => {
  if (typeof window !== "undefined" && !window.Cal) {
    (function (C: any, A: string, L: string) {
      let p = function (a: any, ar: any) {
        a.q.push(ar);
      };
      let d = C.document;
      C.Cal =
        C.Cal ||
        function () {
          let cal = C.Cal;
          let ar = arguments;
          if (!cal.loaded) {
            cal.ns = {};
            cal.q = cal.q || [];
            d.head.appendChild(d.createElement("script")).src = A;
            cal.loaded = true;
          }
          if (ar[0] === L) {
            const api = function () {
              p(api, arguments);
            };
            const namespace = ar[1];
            api.q = api.q || [];
            if (typeof namespace === "string") {
              cal.ns[namespace] = cal.ns[namespace] || api;
              p(cal.ns[namespace], ar);
              p(cal, ["initNamespace", namespace]);
            } else p(cal, ar);
            return;
          }
          p(cal, ar);
        };
    })(window, "https://app.cal.com/embed/embed.js", "init");

    window.Cal("init", "premier-contact-accompagnement-strategique", { origin: "https://app.cal.com" });
    window.Cal.ns["premier-contact-accompagnement-strategique"]("ui", { hideEventTypeDetails: false, layout: "month_view" });
  }
};

const AgenceContenuMontreal = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    initCalEmbed();
  }, []);

  const openCalendar = () => {
    if (window.Cal) {
      window.Cal.ns["premier-contact-accompagnement-strategique"]("openModal", {
        calLink: "maxim-rousseau-6ciolz/premier-contact-accompagnement-strategique",
      });
    }
  };

  const forWho = [
    "Entrepreneurs à Montréal",
    "PME montréalaises",
    "Marques personnelles",
    "Entreprises B2B",
  ];

  const steps = [
    { num: "1", title: "Appel stratégique", desc: "Discussion initiale" },
    { num: "2", title: "Analyse", desc: "Positionnement et objectifs" },
    { num: "3", title: "Stratégie", desc: "Plan de contenu" },
    { num: "4", title: "Création", desc: "Production et diffusion" },
    { num: "5", title: "Optimisation", desc: "Amélioration continue" },
  ];

  const whyLocal = [
    "Une meilleure compréhension du marché local",
    "Un message adapté à la réalité montréalaise",
    "Une communication plus pertinente",
    "Un positionnement plus crédible",
  ];

  const contentPurposes = [
    "Éduquer l'audience",
    "Créer de la confiance",
    "Positionner une expertise",
    "Soutenir la conversion",
  ];

  return (
    <>
      <SEOHead
        title="Agence de création de contenu à Montréal | Contenu stratégique – Agence 80/20"
        description="Agence 80/20 est une agence de création de contenu à Montréal qui aide les entrepreneurs et PME à transformer leur visibilité en leads grâce à du contenu stratégique."
        canonical="/agence-creation-contenu-montreal"
      />

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src={agenceContenuMontreal} 
              alt="Agence de création de contenu à Montréal" 
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
                <Video className="h-4 w-4" />
                <span className="text-sm font-medium">Création de contenu stratégique</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Agence de création de contenu à Montréal <span className="text-primary">orientée résultats</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Agence 80/20 est une agence de création de contenu à Montréal qui accompagne des entrepreneurs et des PME souhaitant utiliser le contenu comme un levier de croissance, et non simplement comme un outil de visibilité.
              </p>
              
              <p className="text-lg text-foreground font-medium mb-8">
                Ici, le contenu est pensé pour attirer, positionner et convertir.
              </p>
              
              <Button 
                size="lg" 
                onClick={openCalendar}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Réserver un appel stratégique
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Créer du contenu à Montréal ne suffit plus
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6">
                À Montréal, les entreprises sont exposées à une quantité massive de contenu chaque jour. Publier sans stratégie mène rarement à des résultats concrets.
              </p>
              
              <p className="text-lg text-foreground font-medium">
                Ce qui fait la différence aujourd'hui, ce n'est pas la quantité de contenu, mais la structure derrière celui-ci.
              </p>
            </motion.div>
          </div>
        </section>

        {/* What We Do Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
                Ce que fait réellement une agence de création de contenu
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8 text-center">
                Une agence de création de contenu efficace ne se limite pas à produire des vidéos ou des publications.
              </p>
              
              <p className="text-lg text-foreground font-medium mb-6 text-center">
                Elle doit :
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  "Comprendre le marché montréalais",
                  "Identifier les enjeux des entrepreneurs",
                  "Traduire une offre en messages clairs",
                  "Créer du contenu aligné sur un objectif précis"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-muted/30 p-4 rounded-lg border border-border">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-lg text-foreground font-medium text-center">
                Chez Agence 80/20, chaque contenu a une intention mesurable.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Approach Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
                Notre approche en création de contenu à Montréal
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-background p-6 rounded-xl border border-border">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Target className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Stratégie avant production</h3>
                  <p className="text-muted-foreground mb-4">
                    Avant de créer quoi que ce soit, on définit le client idéal, le message principal et le rôle précis du contenu dans le parcours client.
                  </p>
                  <p className="text-foreground font-medium text-sm">
                    Sans stratégie, le contenu devient du bruit.
                  </p>
                </div>
                
                <div className="bg-background p-6 rounded-xl border border-border">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Video className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Création de contenu stratégique</h3>
                  <p className="text-muted-foreground mb-4">Nous créons du contenu qui sert à :</p>
                  <ul className="space-y-2">
                    {contentPurposes.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-foreground text-sm">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-muted-foreground mt-4 text-sm">
                    Le contenu est pensé pour être utilisé autant en organique qu'en publicité.
                  </p>
                </div>
                
                <div className="bg-background p-6 rounded-xl border border-border">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <TrendingUp className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Amplification et optimisation</h3>
                  <p className="text-muted-foreground">
                    Un bon contenu mérite d'être vu. Nous aidons à identifier les contenus qui performent et à les amplifier intelligemment afin d'augmenter leur impact.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* For Who Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
                À qui s'adresse cette agence de création de contenu à Montréal
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-primary/5 p-6 rounded-xl border border-primary/20">
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <CheckCircle className="h-6 w-6 text-primary" />
                    Cette approche est conçue pour :
                  </h3>
                  <ul className="space-y-3">
                    {forWho.map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-foreground">
                        <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-muted/50 p-6 rounded-xl border border-border">
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <span className="text-muted-foreground">✕</span>
                    Ce n'est pas pour :
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3 text-muted-foreground">
                      <span className="text-muted-foreground">✕</span>
                      Ceux qui veulent seulement publier sans objectif
                    </li>
                    <li className="flex items-center gap-3 text-muted-foreground">
                      <span className="text-muted-foreground">✕</span>
                      Ceux qui cherchent le prix le plus bas
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Why Local Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
                Pourquoi choisir une agence locale à Montréal
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8 text-center">
                Travailler avec une agence de création de contenu à Montréal permet :
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {whyLocal.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-background p-4 rounded-lg border border-border">
                    <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-lg text-foreground font-medium text-center">
                Agence 80/20 travaille exclusivement avec des entreprises du Québec.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
                Comment débute la collaboration
              </h2>
              
              <div className="grid md:grid-cols-5 gap-4">
                {steps.map((step, index) => (
                  <div key={index} className="text-center">
                    <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-lg">
                      {step.num}
                    </div>
                    <h3 className="font-bold text-foreground mb-1">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.desc}</p>
                  </div>
                ))}
              </div>
              
              <p className="text-lg text-muted-foreground text-center mt-8">
                Chaque étape est orientée vers des résultats concrets.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Prêt à structurer votre création de contenu à Montréal ?
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8">
                Si vous êtes à Montréal et que vous voulez utiliser le contenu comme un véritable levier de croissance, Agence 80/20 peut vous accompagner.
              </p>
              
              <Button 
                size="lg" 
                onClick={openCalendar}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Réserver un appel stratégique
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Related Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
                Pages reliées
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <Link 
                  to="/accompagnement-marketing-montreal"
                  className="bg-muted/30 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Accompagnement marketing à Montréal</h3>
                  <p className="text-sm text-muted-foreground">Notre accompagnement complet pour les entrepreneurs montréalais.</p>
                </Link>
                
                <Link 
                  to="/accompagnement-marketing-quebec"
                  className="bg-muted/30 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Accompagnement marketing au Québec</h3>
                  <p className="text-sm text-muted-foreground">Notre accompagnement pour les entrepreneurs partout au Québec.</p>
                </Link>
                
                <Link 
                  to="/articles/strategie-de-contenu-ventes"
                  className="bg-muted/30 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Stratégie de contenu : transformer l'attention en ventes</h3>
                  <p className="text-sm text-muted-foreground">Comment structurer votre contenu pour générer des ventes.</p>
                </Link>
                
                <Link 
                  to="/articles/contenu-ne-genere-pas-de-clients"
                  className="bg-muted/30 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Pourquoi votre contenu ne génère pas de clients</h3>
                  <p className="text-sm text-muted-foreground">Les erreurs courantes et comment les corriger.</p>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default AgenceContenuMontreal;
